import { useEffect, useMemo, useState } from 'react';

/**
 * Seconds left until `deadline`, ticking every second. `serverNow` corrects for the viewer's
 * clock being off, so everyone sees the same deadline the server enforces.
 */
export function useCountdown(deadline: string | undefined, serverNow?: string) {
  const skewMs = useMemo(
    () => (serverNow ? new Date(serverNow).getTime() - Date.now() : 0),
    // Only re-measure when the server sends a fresh timestamp.
    [serverNow]
  );
  const target = deadline ? new Date(deadline).getTime() : null;
  const compute = () => (target == null ? 0 : Math.max(0, Math.round((target - (Date.now() + skewMs)) / 1000)));
  const [secondsLeft, setSecondsLeft] = useState(compute);

  useEffect(() => {
    setSecondsLeft(compute());
    if (target == null) return;
    const timer = setInterval(() => {
      const next = compute();
      setSecondsLeft(next);
      if (next <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, skewMs]);

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  return {
    secondsLeft,
    expired: target != null && secondsLeft <= 0,
    label: `${minutes}:${seconds.toString().padStart(2, '0')}`,
  };
}
